import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { EventShell } from "@/components/cccd/EventShell"
import { SectionHeader } from "@/components/cccd/SectionHeader"
import { StatusPill } from "@/components/cccd/StatusPill"
import Alert from "@/components/ui/alert/Alert"
import { cccdAdmin } from "@/lib/api/cccdAdminClient"
import { getValidationErrorMessage } from "@/lib/errors"
import { toastError } from "@/lib/toast"
import { slugify } from "@/lib/slugify"
import { durationPill, registrationWindowPill, validateEventDates } from "@/lib/cccdDatePills"

const emptyForm = {
  name: "",
  slug: "",
  eventStartDate: "",
  eventEndDate: "",
  registrationStartDate: "",
  registrationDeadline: "",
  consentText: "",
}

export default function EventFormPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const fromId = searchParams.get("fromId")
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState("")

  const { data: source } = useQuery({
    queryKey: ["cccd-event", fromId],
    queryFn: () => cccdAdmin.getEvent(fromId!),
    enabled: Boolean(fromId),
  })

  useEffect(() => {
    if (!source) return
    const name = `${source.name} (copia)`
    setForm((prev) => ({ ...prev, name, slug: slugify(name), consentText: source.consentText }))
  }, [source])

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      fromId ? cccdAdmin.duplicateEvent(fromId, body) : cccdAdmin.createEvent(body),
    onSuccess: (created) => {
      // Invalidar la lista hace que el evento recién creado aparezca sin un refresh manual.
      qc.invalidateQueries({ queryKey: ["server-table", "/cccd/admin/events"] })
      // Un evento nuevo no trae categorías ni planes (el duplicado sí los copia): se abre en Configuración.
      navigate(`/cccd/events/${created.id}?tab=configuracion`)
    },
    onError: (err) => {
      const message = getValidationErrorMessage(err)
      setError(message)
      toastError(message)
    },
  })

  function f(key: keyof typeof emptyForm) {
    return {
      value: form[key],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value })),
    }
  }

  function goBack() {
    navigate("/cccd/events")
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const invalid = validateEventDates(form)
    if (invalid) {
      setError(invalid)
      return
    }
    setError("")
    createMutation.mutate({
      ...form,
      eventStartDate: new Date(form.eventStartDate).toISOString(),
      eventEndDate: new Date(form.eventEndDate).toISOString(),
      registrationStartDate: new Date(form.registrationStartDate).toISOString(),
      registrationDeadline: new Date(form.registrationDeadline).toISOString(),
    })
  }

  const inputCls = "min-h-11 w-full border border-gray-300 dark:border-gray-700 rounded-lg p-2 text-sm bg-white dark:bg-gray-800"
  const eventDuration = durationPill(form.eventStartDate, form.eventEndDate)
  const registrationWindow = registrationWindowPill(form.registrationStartDate, form.registrationDeadline)

  return (
    <EventShell
      mode="create"
      activeTab="info"
      onTabChange={() => {}}
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        { label: "CCCD", to: "/cccd" },
        { label: "Eventos", to: "/cccd/events" },
        fromId ? "Duplicar evento" : "Nuevo evento",
      ]}
      title={fromId ? "Duplicar evento" : "Nuevo evento"}
      subtitle="Concurso nacional CCCD y su calendario de inscripciones"
    >
      {fromId && source ? (
        <div className="mb-5">
          <Alert
            variant="info"
            title="Clonando estructura de una edición anterior"
            message={`Categorías, planes de pago y el semillero asignado de "${source.name}" se copiarán automáticamente. Cambia el nombre (el identificador se genera a partir de él y debe ser distinto al del evento original) y define las fechas de esta nueva edición.`}
          />
        </div>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4">
        <SectionHeader
          title="Identificación"
          subtitle="Nombre visible del evento — el identificador se genera a partir del nombre"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.8" /><path d="M4 10h16M9 4v6" stroke="currentColor" strokeWidth="1.8" /></svg>}
        >
          <label htmlFor="event-form-name" className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Nombre del evento *</label>
          <input
            id="event-form-name"
            required
            placeholder="Nombre"
            className={inputCls}
            value={form.name}
            onChange={(e) => {
              const name = e.target.value
              setForm((prev) => ({ ...prev, name, slug: slugify(name) }))
            }}
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Identificador: <span className="font-mono">{form.slug || "—"}</span> (debe ser único)
          </p>
        </SectionHeader>

        <SectionHeader
          title="Fechas del concurso"
          subtitle="Cuándo se corre el evento presencialmente"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" /></svg>}
          badge={eventDuration && <StatusPill tone={eventDuration.tone} label={eventDuration.label} />}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="event-form-start" className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Fecha de inicio *</label>
              <input id="event-form-start" required type="datetime-local" className={inputCls} {...f("eventStartDate")} />
            </div>
            <div>
              <label htmlFor="event-form-end" className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Fecha de cierre *</label>
              <input id="event-form-end" required type="datetime-local" className={inputCls} {...f("eventEndDate")} />
            </div>
          </div>
        </SectionHeader>

        <SectionHeader
          title="Inscripciones"
          subtitle="Ventana en la que los equipos pueden registrarse"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /></svg>}
          badge={registrationWindow && <StatusPill tone={registrationWindow.tone} label={registrationWindow.label} />}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="event-form-reg-start" className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Apertura de inscripciones *</label>
              <input id="event-form-reg-start" required type="datetime-local" className={inputCls} {...f("registrationStartDate")} />
            </div>
            <div>
              <label htmlFor="event-form-reg-end" className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Cierre de inscripciones *</label>
              <input id="event-form-reg-end" required type="datetime-local" className={inputCls} {...f("registrationDeadline")} />
            </div>
          </div>
          <p className="mt-2.5 text-xs text-gray-500 dark:text-gray-400">La duración se recalcula en vivo a partir de estas dos fechas.</p>
        </SectionHeader>

        <SectionHeader
          title="Consentimiento"
          subtitle="Texto legal que el equipo debe aceptar al inscribirse"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /></svg>}
        >
          <label htmlFor="event-form-consent" className="sr-only">Texto de consentimiento</label>
          <textarea id="event-form-consent" required rows={3} placeholder="Texto de consentimiento" className={inputCls} {...f("consentText")} />
        </SectionHeader>

        {error ? <Alert variant="error" title="No se pudo crear el evento" message={error} /> : null}

        <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
          <button type="button" onClick={goBack} className="min-h-11 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800">
            Cancelar
          </button>
          <button type="submit" disabled={createMutation.isPending} className="min-h-11 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
            {createMutation.isPending ? "Creando..." : fromId ? "Duplicar evento" : "Crear evento"}
          </button>
        </div>
      </form>
    </EventShell>
  )
}
